import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Download } from 'lucide-react';

// Import navigation links from data file - use the same ones as the landing page
const navLinks = [
  { id: 'home', label: 'Home', path: '/' },
  { id: 'about', label: 'About', path: '/about' },
  { id: 'services', label: 'Services', path: '/services' },
  { id: 'projects', label: 'Projects', path: '/projects' },
  { id: 'careers', label: 'Careers', path: '/careers' },
  { id: 'blog', label: 'Blog', path: '/blog' },
  { id: 'innovation', label: 'Innovation', path: '/innovation' },

  { id: 'contact', label: 'Contact', path: '/contact' }
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  
  // Handle scroll effect with a direct approach
  const handleScroll = useCallback(() => {
    if (window.scrollY > 50) {
      setScrolled(true);
    } else {
      setScrolled(false);
    }
  }, []);
  
  useEffect(() => {
    // Initial check
    handleScroll();
    
    // Add event listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);
  
  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);
  
  // Check if link is active
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-[1000] transition-all duration-500 ${
        scrolled ? 'py-2 md:py-3 bg-white/90 backdrop-blur-md shadow-lg' : 'py-3 md:py-4 bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center z-10 relative">
          <img 
            src={scrolled ? "/img/logo.png" : "/img/logo-white.png"} 
            alt="GALO Industries"
            className={`h-10 md:h-12 lg:h-14 transition-all duration-300 ${!scrolled && 'opacity-95 filter brightness-0 invert'}`}
          />
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-4 xl:space-x-6">
          {navLinks.map(link => (
            <Link
              key={link.id}
              to={link.path}
              className={`relative px-2 py-1 text-sm font-medium transition-colors tracking-wide group ${
                isActive(link.path)
                  ? scrolled 
                    ? 'text-blue-600' 
                    : 'text-white'
                  : scrolled 
                    ? 'text-gray-700 hover:text-blue-600' 
                    : 'text-white/90 hover:text-white'
              }`}
            >
              {link.label}
              <span 
                className={`absolute -bottom-1 left-0 h-0.5 bg-current transition-all duration-300 ${
                  isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'
                }`}
              ></span>
            </Link>
          ))}
          
         
        </nav>
        
        {/* Mobile Menu Button */}
        <button
          type="button"
          className="lg:hidden z-50 w-10 h-10 flex items-center justify-center"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          {menuOpen ? (
            <X className={`w-6 h-6 ${scrolled ? 'text-gray-800' : 'text-white'}`} />
          ) : (
            <Menu className={`w-6 h-6 ${scrolled ? 'text-gray-800' : 'text-white'}`} />
          )}
        </button>
      </div>
      
      {/* Mobile Menu - Simple dropdown that works reliably */}
      <div 
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          menuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        } ${scrolled ? 'bg-white' : 'bg-gray-900'}`}
        style={{ boxShadow: menuOpen ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none' }}
      >
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <nav className="flex flex-col space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.id}
                to={link.path}
                className={`text-base font-medium py-2 px-3 rounded ${
                  isActive(link.path) 
                    ? scrolled ? 'bg-blue-50 text-blue-600' : 'bg-gray-800 text-white' 
                    : scrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-300 hover:bg-gray-800'
                }`}
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            
          
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;